import React from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { StyledBottomAppBar, StyleIconBtn } from "./shared/styles";
import Badge from "../badge/Badge";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetProjectMembershipQuery } from "../../app/accounts/memberships";
import { useGetProjectQuery } from "../../app/services/projects";
import { isSpecialistPosition } from "../../utils/memberPositionUtils";
import useProjectDmAccess from "../../hooks/useProjectDmAccess";
import useTodoCount from "../../hooks/useTodoCount";
import useTalkroomBadgeCounts from "../../hooks/useTalkroomBadgeCounts";
import { useProjectPanelTabs } from "../project/useProjectPanelTabs";
import {
  PROJECT_NAV_ITEMS,
  getVisibleProjectNavItemIds,
  type ProjectNavItemId,
} from "../project/projectNavItems";

interface BottomAppBarProjectProps {
  scrollEnabled?: boolean;
  onTabChange?: (tab: string) => void;
  activeTab?: string;
  isPreviewRoute?: boolean;
}

interface LayoutState {
  layout: string;
}

const BottomAppBarProject: React.FC<BottomAppBarProjectProps> = ({
  scrollEnabled = true,
  onTabChange,
  activeTab = "home",
  isPreviewRoute = false,
}) => {
  const scrollState = useSelector((state: LayoutState) => state.layout);
  const { projectId } = useParams<{ projectId: string }>();

  // RTK Query でユーザーとメンバーシップ情報を取得
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(projectId ?? "", {
    skip: !projectId || !currentUserData,
  });
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId || !currentUserData },
  );

  // メンバーシップからポジションを取得
  const positionStr: string | undefined = membershipData?.data?.position ?? undefined;

  // 納品物なし（チェックポイント0）のプロジェクトはホームを使わず、
  // トークルーム中心のやりとりになるため「ホーム」タブを非表示にする。
  const noDeliverable = projectData?.data?.project?.total_shots_count === 0;

  const skipBadge = !projectId || !positionStr;
  const { count: todoCount } = useTodoCount(projectId, positionStr, skipBadge);
  // 未読数・未ダウンロード数は BE のグループ(position)単位集計から取得（完了は未読から除外済み）
  const { unreadCount, undownloadCount } = useTalkroomBadgeCounts(projectId, skipBadge);

  // DM タブの可否（REVIEWER は参加中の Chat スレッドがあるときのみ表示）。
  // useProjectShortcuts（⌥5）と同じ hook を使い、表示とショートカットを揃える。
  const { canAccessDm } = useProjectDmAccess(projectId);

  // ノート / ライブラリはこのプロジェクトに共有されている（または作成・編集
  // 権限がある）ときだけ出す。判定はショートカット（⌥8 / ⌥9）と共用。
  const { showNotes, showLibrary } = useProjectPanelTabs();

  const bottomAppBarBottom = !scrollEnabled
    ? 0
    : scrollState === "down"
      ? -80
      : 0;

  // タブ変更ハンドラー（Activity 切り替え）
  const handleNavigation = (path: string) => {
    onTabChange?.(path);
  };

  // 表示可否の判定はタスクボードのプロジェクト見出し（ProjectNavLinks）と
  // 共通の純粋関数に寄せる（projectNavItems.ts）。
  const visibleNavItems = getVisibleProjectNavItemIds({
    position: positionStr,
    noDeliverable,
    canAccessDm,
    showNotes,
    showLibrary,
  });
  const navItems = PROJECT_NAV_ITEMS.filter((item) =>
    visibleNavItems.includes(item.id),
  );

  // スペシャリストは DM しか持たないため、ノート / ライブラリのどちらも
  // 共有されていなければバー自体を出さない（DM への遷移は不要）。
  if (
    positionStr &&
    isSpecialistPosition(positionStr) &&
    !showNotes &&
    !showLibrary
  ) {
    return null;
  }
  if (navItems.length === 0) return null;

  // バッジ描画: home=ToDo、talkroom=未読＋未ダウンロードの合算（いずれも青の単一バッジ）
  const renderBadges = (itemId: ProjectNavItemId) => {
    if (itemId === "home") {
      return todoCount > 0 ? <Badge count={todoCount} notification /> : null;
    }
    if (itemId === "talkroom") {
      const talkroomCount = unreadCount + undownloadCount;
      return talkroomCount > 0 ? <Badge count={talkroomCount} notification /> : null;
    }
    return null;
  };

  return (
    <StyledBottomAppBar $bottomAppBarBottom={bottomAppBarBottom}>
      {navItems.map((item) => {
        const isActive = !isPreviewRoute && activeTab === item.path;
        return (
          <StyleIconBtn
            key={item.id}
            $isActive={isActive}
            onClick={() => handleNavigation(item.path)}
          >
            <div className="material-symbols-rounded">{item.icon}</div>
            <div className="label8">{item.label}</div>
            {renderBadges(item.id)}
          </StyleIconBtn>
        );
      })}
    </StyledBottomAppBar>
  );
};

export default BottomAppBarProject;
